/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck
/**
 * RoutesListView — Milestone 2 routes list with filter/sort UI.
 *
 * Mirrors the pattern from VotersView.tsx.
 * Filter/sort spec: memory-bank/plans/filter-sort-ui-plan.md
 * API contract: memory-bank/plans/filter-sort-plan.md
 *
 * Layout:
 *   [H1: Routes] [Export CSV ⌄]
 *   [FilterBar: search, status, precinct, subregion, assigned to (hide for canvassers)]
 *   [Active filter chips + Clear all]
 *   [DataTable: Priority | Name | Subregion | Precinct | Doors | Expected ballots | Assigned to | Status | Updated]
 *   [ResultsCount + pagination]
 *
 * URL-synced, server-side paginated (50 per page).
 * Canvassers see only routes where assigned_canvasser_id == user.id (row-filter in backend).
 */
import React, { useEffect, useState } from 'react';
import { Archive, Plus, Route as RouteIcon, Users } from 'lucide-react';
import type { Route } from '@/types';
import client from '@/api/client';
import { useListQuery } from '@/hooks/useListQuery';
import { useAuth } from '@/context/AuthContext';
import DataTable from '@/components/DataTable';
import RouteDetailDrawer from '@/components/RouteDetailDrawer';
import EmptyState from '@/components/EmptyState';
import AutoplanRoutesModal from '@/components/routes/AutoplanRoutesModal';
import StaffWeekendModal from '@/components/routes/StaffWeekendModal';
import {
  FilterBar,
  SearchInput,
  FilterSelect,
  FilterMultiSelect,
} from '@/components/filters';

interface PrecinctOption {
  value: string;
  label: string;
}

interface SubregionOption {
  value: string;
  label: string;
}

interface CanvasserOption {
  value: string;
  label: string;
}

/**
 * RoutesListView — server-paginated routes list with advanced filtering.
 */
export function RoutesListView({ onBack }: { onBack?: () => void }) {
  // ============================================================================
  // State
  // ============================================================================
  const { user } = useAuth();
  const [precinctOptions, setPrecinctOptions] = useState<PrecinctOption[]>([]);
  const [subregionOptions, setSubregionOptions] = useState<SubregionOption[]>([]);
  const [canvasserOptions, setCanvasserOptions] = useState<CanvasserOption[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  // Selected route → opens the RouteDetailDrawer (null = closed).
  const [selectedRouteId, setSelectedRouteId] = useState<number | null>(null);
  // Route id currently being archived → disables its button (double-click guard).
  const [archivingId, setArchivingId] = useState<number | null>(null);
  // "Create routes" (autoplan) modal. Coordinator+ only — the endpoint is
  // gated to ROUTE_WRITE_ROLES, so showing a canvasser a button that only
  // 403s would be a dead end (same reasoning as the Archive column below).
  const [autoplanOpen, setAutoplanOpen] = useState(false);
  // "Staff the weekend" (Easy button #1). Same coordinator+ gate as autoplan —
  // POST /routes/staff is ROUTE_WRITE_ROLES.
  const [staffOpen, setStaffOpen] = useState(false);
  const [autoplanNotice, setAutoplanNotice] = useState<string | null>(null);

  // Canvassers get a read-only view: the "Assigned to" filter, the archive
  // action, and the Create routes button are all coordinator+ concerns.
  // Declared here because the options effect below reads it.
  const isCanvasser = user?.role === 'canvasser';

  // ============================================================================
  // Query hook: server-side list query with envelope=v2
  // ============================================================================
  const query = useListQuery<Route>({
    endpoint: '/routes',
    initialParams: { envelope: 'v2' },
    pageSize: 50,
  });

  // ============================================================================
  // Load dropdown options on mount
  // ============================================================================
  useEffect(() => {
    async function loadOptions() {
      try {
        setLoadingOptions(true);

        // GET /routes/canvassers — id + full_name only, gated to the same
        // ROUTE_WRITE_ROLES as route creation. This replaces the long-standing
        // TODO here: /admin/users needs a provisioner, so a COORDINATOR (the
        // person who actually plans walks) could never populate this list.
        // Canvassers themselves get a 403, hence the role guard.
        if (!isCanvasser) {
          try {
            const res = await client.get('/routes/canvassers');
            setCanvasserOptions(
              (res.data || []).map((u: any) => ({
                value: String(u.id),
                label: u.full_name,
              })),
            );
          } catch {
            // Degrade to a hidden filter and an unavailable shift picker rather
            // than failing the whole view over a dropdown.
            setCanvasserOptions([]);
          }
        }

        // Precincts/subregions still come from free-text filtering; there is no
        // metadata endpoint for them yet.
        setPrecinctOptions([]);
        setSubregionOptions([]);
      } catch (err) {
        console.error('Failed to load route options:', err);
        // Gracefully degrade: empty dropdowns
      } finally {
        setLoadingOptions(false);
      }
    }

    loadOptions();
  }, [isCanvasser]);

  // ============================================================================
  // Handle filter changes
  // ============================================================================
  const handleSearchChange = (value: string) => {
    query.setSearch(value);
  };

  const handleStatusChange = (values: string[]) => {
    query.setFilter('status', values.length > 0 ? values : null);
  };

  const handlePrecinctChange = (value: string | null) => {
    if (value) {
      query.setFilter('precinct_num', value);
    } else {
      query.setFilter('precinct_num', null);
    }
  };

  const handleSubregionChange = (value: string | null) => {
    if (value) {
      query.setFilter('subregion', value);
    } else {
      query.setFilter('subregion', null);
    }
  };

  const handleAssignedToChange = (value: string | null) => {
    if (value) {
      query.setFilter('assigned_canvasser_id', Number(value));
    } else {
      query.setFilter('assigned_canvasser_id', null);
    }
  };

  // ============================================================================
  // Archive = this app's delete
  // ============================================================================
  // There is no DELETE endpoint on routes; archiving is the soft-delete, and
  // GET /routes now hides archived rows unless `status` explicitly asks for
  // them. So a refetch after the PATCH makes the row disappear from the
  // default list, and the Status filter's "Archived" option is how you get it
  // back (then PATCH it to another status to restore).
  const handleArchive = async (route: Route) => {
    const confirmed = window.confirm(
      `Archive "${route.name}"? It leaves this list but is not deleted — ` +
        'filter Status → Archived to find it again.'
    );
    if (!confirmed) return;

    setArchivingId(route.id);
    try {
      await client.patch(`/routes/${route.id}`, { status: 'archived' });
      query.refetch();
    } catch (err) {
      console.error('Failed to archive route:', err);
      window.alert('Could not archive that route. Please try again.');
    } finally {
      setArchivingId(null);
    }
  };

  // ============================================================================
  // Extract current filter values
  // ============================================================================
  const currentFilters = query.filters as Record<string, any>;
  const currentStatus = Array.isArray(currentFilters.status)
    ? currentFilters.status
    : currentFilters.status
      ? [currentFilters.status]
      : [];
  const currentPrecinct = currentFilters.precinct_num || null;
  const currentSubregion = currentFilters.subregion || null;
  const currentAssignedTo = currentFilters.assigned_canvasser_id
    ? String(currentFilters.assigned_canvasser_id)
    : null;

  // ============================================================================
  // Build active filter chips
  // ============================================================================
  const activeFilterChips: Array<{ id: string; label: string; value: string }> = [];

  if (query.search) {
    activeFilterChips.push({
      id: 'search',
      label: 'Search',
      value: query.search,
    });
  }

  if (currentStatus.length > 0) {
    activeFilterChips.push({
      id: 'status',
      label: 'Status',
      value: currentStatus.join(', '),
    });
  }

  if (currentPrecinct) {
    activeFilterChips.push({
      id: 'precinct',
      label: 'Precinct',
      value: currentPrecinct,
    });
  }

  if (currentSubregion) {
    activeFilterChips.push({
      id: 'subregion',
      label: 'Subregion',
      value: currentSubregion,
    });
  }

  if (currentAssignedTo) {
    const canvasserLabel =
      canvasserOptions.find((c) => c.value === currentAssignedTo)?.label || currentAssignedTo;
    activeFilterChips.push({
      id: 'assigned_to',
      label: 'Assigned to',
      value: canvasserLabel,
    });
  }

  // ============================================================================
  // Table columns definition
  // ============================================================================
  const columns = [
    {
      key: 'priority_score',
      header: 'Priority',
      sortable: true,
      render: (row: Route) => (
        <div className="text-sm font-semibold text-status-success">
          {row.door_count > 0 ? `${row.door_count} doors` : '—'}
        </div>
      ),
    },
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      render: (row: Route) => (
        <div className="font-medium text-fg-primary">{row.name}</div>
      ),
    },
    {
      key: 'subregion',
      header: 'Subregion',
      sortable: false,
      render: (row: Route) => (
        <div className="text-sm text-fg-secondary">{row.subregion || '—'}</div>
      ),
    },
    {
      key: 'precinct_num',
      header: 'Precinct',
      sortable: false,
      render: (row: Route) => (
        <div className="text-sm text-fg-secondary">
          {row.precinct_num ? `P${row.precinct_num}` : '—'}
        </div>
      ),
    },
    {
      key: 'door_count',
      header: 'Doors',
      sortable: true,
      render: (row: Route) => (
        <div className="text-sm font-mono text-fg-secondary">{row.door_count}</div>
      ),
    },
    {
      key: 'expected_ballots',
      header: 'Expected ballots',
      sortable: false,
      render: (row: Route) => (
        <div className="text-sm text-fg-secondary">—</div>
      ),
    },
    {
      key: 'assigned_canvasser',
      header: 'Assigned to',
      sortable: false,
      render: (row: Route) => (
        <div className="text-sm text-fg-secondary">
          {row.assigned_canvasser?.full_name || '—'}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (row: Route) => (
        <StatusBadge status={row.status} />
      ),
    },
    {
      key: 'updated_at',
      header: 'Updated',
      sortable: true,
      render: (row: Route) =>
        row.updated_at
          ? new Date(row.updated_at).toLocaleDateString()
          : '—',
    },
    // Archive action. Coordinators+ only — canvassers can read routes but the
    // PATCH is gated to ROUTE_WRITE_ROLES, so showing them a button that only
    // 403s would be a dead end. `hideOnCard` keeps it out of the mobile card
    // layout: cutting and clearing turf is a desk task, and an icon column
    // renders as a stray empty-labelled box on a card.
    ...(isCanvasser
      ? []
      : [
          {
            key: 'actions',
            header: '',
            sortable: false,
            hideOnCard: true,
            className: 'w-12',
            render: (row: Route) =>
              row.status === 'archived' ? null : (
                <button
                  type="button"
                  title="Archive route"
                  aria-label={`Archive ${row.name}`}
                  disabled={archivingId === row.id}
                  onClick={(e) => {
                    // The row itself opens the detail drawer.
                    e.stopPropagation();
                    handleArchive(row);
                  }}
                  className="p-1.5 rounded text-fg-muted hover:text-status-danger hover:bg-bg-elevated/60 transition-colors disabled:opacity-40 disabled:pointer-events-none"
                >
                  <Archive className="w-4 h-4" />
                </button>
              ),
          },
        ]),
  ];

  // ============================================================================
  // Render
  // ============================================================================
  const createRoutesButton = isCanvasser ? null : (
    <div className="flex shrink-0 items-center gap-2">
      {/* Staff is secondary to Create: you cannot staff routes that do not
          exist yet, so a coordinator's first press here is nearly always
          Create. Once the routes exist, Staff is the weekly one. */}
      <button
        type="button"
        onClick={() => setStaffOpen(true)}
        className="inline-flex shrink-0 items-center gap-2 rounded-md border border-border-subtle px-3 py-2 text-sm font-semibold text-fg-primary hover:bg-bg-base"
      >
        <Users size={16} />
        Staff routes
      </button>
      <button
        type="button"
        onClick={() => setAutoplanOpen(true)}
        className="inline-flex shrink-0 items-center gap-2 rounded-md bg-accent px-3 py-2 text-sm font-semibold text-white hover:opacity-90"
      >
        <Plus size={16} />
        Create routes
      </button>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header. The primary action lives here rather than only in the empty
          state: a coordinator planning next weekend's walks has a full routes
          list, so an empty-state-only button would be unreachable exactly when
          it is wanted most. min-w-0 on the text side so the button never gets
          squeezed off a 375px screen. */}
      {createRoutesButton && (
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-fg-primary">Routes</h1>
            <p className="text-sm text-fg-secondary">
              Walking routes for your campaign.
            </p>
          </div>
          {createRoutesButton}
        </div>
      )}

      {autoplanNotice && (
        <div
          role="status"
          className="rounded-md border border-status-success/40 bg-status-success/10 px-3 py-2 text-sm text-fg-primary"
        >
          {autoplanNotice}
        </div>
      )}

      {/* Main filter bar */}
      <FilterBar
        activeFilters={activeFilterChips}
        onClearAll={query.clearAll}
      >
        <SearchInput
          value={query.search}
          onChange={handleSearchChange}
          placeholder="Search route name / canvasser"
        />

        <FilterMultiSelect
          label="Status"
          values={currentStatus}
          options={[
            { value: 'draft', label: 'Draft' },
            { value: 'active', label: 'Active' },
            { value: 'completed', label: 'Completed' },
            { value: 'archived', label: 'Archived' },
          ]}
          onChange={handleStatusChange}
        />

        <FilterSelect
          label="Precinct"
          value={currentPrecinct}
          options={precinctOptions}
          onChange={handlePrecinctChange}
          placeholder="All precincts"
        />

        <FilterSelect
          label="Subregion"
          value={currentSubregion}
          options={subregionOptions}
          onChange={handleSubregionChange}
          placeholder="All subregions"
        />

        {/* Hide "Assigned to" filter for canvassers */}
        {!isCanvasser && (
          <FilterSelect
            label="Assigned to"
            value={currentAssignedTo}
            options={canvasserOptions}
            onChange={handleAssignedToChange}
            placeholder="All canvassers"
          />
        )}
      </FilterBar>

      {/* Data table (or EmptyState when truly empty with no active filters) */}
      {!query.loading &&
      query.items.length === 0 &&
      !query.search &&
      activeFilterChips.length === 0 ? (
        <EmptyState
          icon={RouteIcon}
          title="No routes yet"
          description={
            isCanvasser
              ? 'Nothing has been assigned to you yet — check with your coordinator.'
              : 'Press Create routes and we will lay out walking routes across your district. You can adjust them afterwards.'
          }
        />
      ) : (
        <DataTable<Route>
          columns={columns}
          query={query}
          rowKey={(row) => row.id}
          emptyMessage="No routes in this campaign."
          emptyFilteredMessage="No routes match these filters."
          mobileMode="cards"
          onRowClick={(row) => setSelectedRouteId(row.id)}
        />
      )}

      {staffOpen && (
        <StaffWeekendModal
          onClose={() => setStaffOpen(false)}
          onStaffed={(assigned, shifts) => {
            setStaffOpen(false);
            setAutoplanNotice(
              `Assigned ${assigned} route${assigned === 1 ? '' : 's'}` +
                (shifts ? ` and created ${shifts} shift${shifts === 1 ? '' : 's'}` : '') +
                '.',
            );
            query.refetch?.();
          }}
        />
      )}

      {autoplanOpen && (
        <AutoplanRoutesModal
          canvassers={canvasserOptions.map((o) => ({
            id: Number(o.value),
            full_name: o.label,
          }))}
          onClose={() => setAutoplanOpen(false)}
          onCreated={(routeCount, shiftCount) => {
            setAutoplanOpen(false);
            setAutoplanNotice(
              `Created ${routeCount} draft ${routeCount === 1 ? 'route' : 'routes'}` +
                (shiftCount > 0
                  ? ` and scheduled ${shiftCount} ${shiftCount === 1 ? 'shift' : 'shifts'}.`
                  : '. Review them, then set them active when you are ready.'),
            );
            // Routes are created as drafts, and the default list already shows
            // drafts — so a plain refetch surfaces them without touching filters.
            query.refetch?.();
          }}
        />
      )}

      {/* Click a route row/card → slide-over with the route's map + stops */}
      <RouteDetailDrawer
        routeId={selectedRouteId}
        onClose={() => setSelectedRouteId(null)}
      />
    </div>
  );
}

export default RoutesListView;

// ============================================================================
// Sub-components
// ============================================================================

function StatusBadge({ status }: { status: string }) {
  const statusColors: Record<string, { bg: string; text: string }> = {
    draft: { bg: 'bg-bg-elevated/50', text: 'text-fg-secondary' },
    active: { bg: 'bg-status-success-bg/30', text: 'text-status-success' },
    completed: { bg: 'bg-status-info-bg/30', text: 'text-status-info' },
    archived: { bg: 'bg-bg-elevated/50', text: 'text-fg-muted' },
  };

  const { bg, text } = statusColors[status] || { bg: 'bg-bg-elevated/50', text: 'text-fg-secondary' };

  return (
    <span
      className={`inline-block px-2 py-1 rounded text-[11px] font-semibold uppercase ${bg} ${text}`}
    >
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}
